<template>
	<section class="view-subscription-offers">
		<!-- Hero Section -->
		<header class="hero">
			<h1 class="title">Unlock Premium Features</h1>
			<p class="description">
				Take your game to the next level with ChalkySticks Premium. Get access to advanced features, exclusive
				content, and more.
			</p>
		</header>

		<!-- Billing Cycle Toggle -->
		<div class="billing-toggle">
			<span class="label" v-bind:class="{ active: !isYearly }" v-on:click="isYearly = false">Monthly</span>

			<label class="switch-container">
				<input type="checkbox" class="switch" v-model="isYearly" />
				<span class="slider"></span>
			</label>

			<span class="label" v-bind:class="{ active: isYearly }" v-on:click="isYearly = true">
				Yearly
				<span class="badge">30% off</span>
			</span>
		</div>

		<!-- Plan Cards -->
		<div class="plan-cards">
			<article
				class="plan-card"
				v-bind:class="{ selected: selectedPlan === 'plus' }"
				v-on:click="Handle_OnSelectPlan('plus')"
			>
				<h3 class="plan-name">Plus</h3>
				<p class="plan-price">
					<span class="amount">{{ plusPrice }}</span>
					<span class="period">/ {{ billingPeriod }}</span>
				</p>
				<ul class="plan-features">
					<li v-for="feature in plans.plus.features" v-bind:key="feature">
						{{ feature }}
					</li>
				</ul>
				<button
					class="select-button"
					v-bind:class="{ selected: selectedPlan === 'plus' }"
					v-on:click.stop="Handle_OnSubscribe('plus')"
				>
					{{ selectedPlan === 'plus' ? 'Selected' : 'Select Plus' }}
				</button>
			</article>

			<article
				class="plan-card recommended"
				v-bind:class="{ selected: selectedPlan === 'pro' }"
				v-on:click="Handle_OnSelectPlan('pro')"
			>
				<span class="recommended-badge">Recommended</span>
				<h3 class="plan-name">Pro</h3>
				<p class="plan-price">
					<span class="amount">{{ proPrice }}</span>
					<span class="period">/ {{ billingPeriod }}</span>
				</p>
				<ul class="plan-features">
					<li v-for="feature in plans.pro.features" v-bind:key="feature">
						{{ feature }}
					</li>
				</ul>
				<button
					class="select-button"
					v-bind:class="{ selected: selectedPlan === 'pro' }"
					v-on:click.stop="Handle_OnSubscribe('pro')"
				>
					{{ selectedPlan === 'pro' ? 'Selected' : 'Select Pro' }}
				</button>
			</article>
		</div>

		<!-- Feature Comparison Table -->
		<section class="feature-comparison">
			<h2 class="comparison-title">Compare Plans</h2>
			<table class="comparison-table">
				<thead>
					<tr>
						<th class="feature-name">Feature</th>
						<th class="plan-column">Plus</th>
						<th class="plan-column">Pro</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="feature in featureComparison" v-bind:key="feature.name">
						<td class="feature-name">{{ feature.name }}</td>
						<td class="plan-column">
							<span v-if="feature.plus" class="check">&#10003;</span>
							<span v-else class="dash">&mdash;</span>
						</td>
						<td class="plan-column">
							<span v-if="feature.pro" class="check">&#10003;</span>
							<span v-else class="dash">&mdash;</span>
						</td>
					</tr>
				</tbody>
			</table>
		</section>
	</section>
</template>

<script lang="ts">
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop } from 'vue-property-decorator';

	/**
	 * Subscription offers view displaying pricing tiers and feature comparison.
	 * Allows users to toggle between monthly/yearly billing and select a plan.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Subscription
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewSubscriptionOffers extends ViewBase {
		/**
		 * Feature comparison data for the comparison table
		 *
		 * @type Array<{ name: string; plus: boolean; pro: boolean }>
		 */
		protected featureComparison: Array<{ name: string; plus: boolean; pro: boolean }> = [
			{
				name: 'Placeholder Feature 1',
				plus: true,
				pro: true,
			},
			{
				name: 'Placeholder Feature 2',
				plus: true,
				pro: true,
			},
			{
				name: 'Placeholder Feature 3',
				plus: true,
				pro: true,
			},
			{
				name: 'Placeholder Feature 4',
				plus: false,
				pro: true,
			},
			{
				name: 'Placeholder Feature 5',
				plus: false,
				pro: true,
			},
			{
				name: 'Placeholder Feature 6',
				plus: false,
				pro: true,
			},
		];

		/**
		 * Whether the yearly billing cycle is selected
		 *
		 * @type boolean
		 */
		protected isYearly: boolean = true;

		/**
		 * Plan configuration with pricing and features
		 *
		 * @type object
		 */
		protected plans: Record<
			string,
			{ features: string[]; monthlyPrice: string; name: string; yearlyPrice: string }
		> = {
			plus: {
				features: ['Placeholder Feature 1', 'Placeholder Feature 2', 'Placeholder Feature 3'],
				monthlyPrice: '$4.99',
				name: 'Plus',
				yearlyPrice: '$34.99',
			},
			pro: {
				features: [
					'Placeholder Feature 1',
					'Placeholder Feature 2',
					'Placeholder Feature 3',
					'Placeholder Feature 4',
					'Placeholder Feature 5',
					'Placeholder Feature 6',
				],
				monthlyPrice: '$9.99',
				name: 'Pro',
				yearlyPrice: '$69.99',
			},
		};

		/**
		 * Currently selected plan
		 *
		 * @type string | null
		 */
		protected selectedPlan: string | null = null;

		/**
		 * Get the billing period label
		 *
		 * @return string
		 */
		protected get billingPeriod(): string {
			return this.isYearly ? 'year' : 'month';
		}

		/**
		 * Get the Plus plan price based on billing cycle
		 *
		 * @return string
		 */
		protected get plusPrice(): string {
			return this.isYearly ? this.plans.plus.yearlyPrice : this.plans.plus.monthlyPrice;
		}

		/**
		 * Get the Pro plan price based on billing cycle
		 *
		 * @return string
		 */
		protected get proPrice(): string {
			return this.isYearly ? this.plans.pro.yearlyPrice : this.plans.pro.monthlyPrice;
		}

		/**
		 * Handle plan selection
		 *
		 * @param string plan
		 * @return void
		 */
		protected Handle_OnSelectPlan(plan: string): void {
			this.selectedPlan = plan;

			this.$emit('select-plan', {
				cycle: this.isYearly ? 'yearly' : 'monthly',
				plan: plan,
			});
		}

		/**
		 * Handle subscribe action
		 *
		 * @param string plan
		 * @return void
		 */
		protected Handle_OnSubscribe(plan: string): void {
			this.selectedPlan = plan;

			this.$emit('subscribe', {
				cycle: this.isYearly ? 'yearly' : 'monthly',
				plan: plan,
			});
		}
	}
</script>

<style lang="scss">
	.view-subscription-offers {
		padding: 2rem 1rem;

		// Hero Section
		// -------------------------------------------------------------------------

		.hero {
			margin-bottom: 2rem;
			text-align: center;

			.title {
				margin-bottom: 0.5rem;
			}

			.description {
				color: var(--chalky-grey-2);
				margin: 0 auto;
				max-width: 500px;
			}
		}

		// Billing Toggle
		// -------------------------------------------------------------------------

		.billing-toggle {
			align-items: center;
			display: flex;
			gap: 1rem;
			justify-content: center;
			margin-bottom: 2rem;

			.label {
				color: var(--chalky-grey-2);
				cursor: pointer;
				transition: color 0.2s ease;

				&.active {
					color: var(--chalky-white);
				}
			}

			.badge {
				background-color: var(--chalky-green);
				border-radius: 4px;
				color: var(--chalky-white);
				display: inline-block;
				font-size: 0.75rem;
				margin-left: 0.5rem;
				padding: 0.125rem 0.5rem;
			}

			.switch-container {
				display: inline-block;
				height: 24px;
				position: relative;
				width: 48px;

				.switch {
					height: 0;
					opacity: 0;
					width: 0;

					&:checked + .slider {
						background-color: var(--chalky-blue);
					}

					&:checked + .slider:before {
						transform: translateX(24px);
					}
				}

				.slider {
					background-color: var(--chalky-grey-3);
					border-radius: 24px;
					bottom: 0;
					cursor: pointer;
					left: 0;
					position: absolute;
					right: 0;
					top: 0;
					transition: 0.3s;

					&:before {
						background-color: var(--chalky-white);
						border-radius: 50%;
						bottom: 2px;
						content: '';
						height: 20px;
						left: 2px;
						position: absolute;
						transition: 0.3s;
						width: 20px;
					}
				}
			}
		}

		// Plan Cards
		// -------------------------------------------------------------------------

		.plan-cards {
			display: grid;
			gap: 1rem;
			grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
			margin: 0 auto 2rem auto;
			max-width: 700px;
		}

		.plan-card {
			background-color: var(--chalky-blue-3);
			border: 2px solid transparent;
			border-radius: 12px;
			cursor: pointer;
			padding: 1.5rem;
			position: relative;
			text-align: center;
			transition: border-color 0.2s ease, transform 0.2s ease;

			&:hover {
				border-color: var(--chalky-grey-3);
			}

			&.selected {
				border-color: var(--chalky-blue);
			}

			&.recommended {
				border-color: var(--chalky-blue);

				.recommended-badge {
					background-color: var(--chalky-blue);
					border-radius: 4px;
					color: var(--chalky-white);
					font-size: 0.75rem;
					left: 50%;
					padding: 0.25rem 0.75rem;
					position: absolute;
					top: -12px;
					transform: translateX(-50%);
				}
			}

			.plan-name {
				margin-bottom: 0.5rem;
			}

			.plan-price {
				margin-bottom: 1rem;

				.amount {
					font-size: 2rem;
					font-weight: bold;
				}

				.period {
					color: var(--chalky-grey-2);
				}
			}

			.plan-features {
				list-style: none;
				margin: 0 0 1.5rem 0;
				padding: 0;
				text-align: left;

				li {
					color: var(--chalky-grey-2);
					padding: 0.25rem 0;

					&:before {
						color: var(--chalky-green);
						content: '\2713';
						margin-right: 0.5rem;
					}
				}
			}

			.select-button {
				background-color: var(--chalky-blue);
				border: none;
				border-radius: 8px;
				color: var(--chalky-white);
				cursor: pointer;
				font-size: 1rem;
				padding: 0.75rem 1.5rem;
				transition: background-color 0.2s ease;
				width: 100%;

				&:hover {
					background-color: var(--chalky-blue-2);
				}

				&.selected {
					background-color: var(--chalky-green);
				}
			}
		}

		// Feature Comparison Table
		// -------------------------------------------------------------------------

		.feature-comparison {
			margin: 0 auto;
			max-width: 700px;

			.comparison-title {
				margin-bottom: 1rem;
				text-align: center;
			}

			.comparison-table {
				border-collapse: collapse;
				width: 100%;

				th,
				td {
					border-bottom: 1px solid var(--chalky-grey-3);
					padding: 0.75rem;
					text-align: center;
				}

				th {
					color: var(--chalky-grey-2);
					font-weight: normal;
				}

				.feature-name {
					text-align: left;
				}

				.check {
					color: var(--chalky-green);
					font-size: 1.25rem;
				}

				.dash {
					color: var(--chalky-grey-3);
				}
			}
		}
	}
</style>
